import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Badge } from './components/ui/badge';
import { useToast } from './hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  // Admin toggles (persistent per admin user)
  const [adminSettings, setAdminSettings] = useState({
    maskPII: true,
    readOnlyMode: true,
    dangerZone: false
  });

  // State management
  const [activeTab, setActiveTab] = useState('organizations');
  const [activeOrgTab, setActiveOrgTab] = useState('restaurants');
  const [activeUserTab, setActiveUserTab] = useState('restaurant-users');
  const [activeComplianceTab, setActiveComplianceTab] = useState('india');

  // Data states
  const [summary, setSummary] = useState({
    restaurants: { total: 0, active: 0, pending: 0, suspended: 0 },
    ngos: { total: 0, active: 0, pending: 0, suspended: 0 },
    volunteers: { total: 0, active: 0, offline: 0 },
    incidents: { failedLogins: 0, lockedAccounts: 0, docExpiries: 0 }
  });

  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);

  // Filter states
  const [orgFilters, setOrgFilters] = useState({
    search: '',
    country: 'all',
    city: 'all',
    status: 'all',
    docValidity: 'all'
  });

  const [userFilters, setUserFilters] = useState({
    search: '',
    role: 'all',
    status: 'all',
    country: 'all',
    city: 'all'
  });

  // Modal states
  const [selectedItem, setSelectedItem] = useState(null);
  const [showProfileDrawer, setShowProfileDrawer] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [impersonateReason, setImpersonateReason] = useState('');
  const [exportReason, setExportReason] = useState('');

  // Check authentication and fetch user data
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (response.data.role !== 'super_admin') {
          navigate('/auth');
          return;
        }
        setUser(response.data);
        await loadAdminSettings();
        await loadSummary();
      } catch (error) {
        console.error('Auth check failed:', error);
        navigate('/auth');
      } finally {
        setAuthLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  // Load admin settings from backend
  const loadAdminSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`, { withCredentials: true });
      setAdminSettings(response.data);
    } catch (error) {
      console.error('Failed to load admin settings:', error);
    }
  };

  // Load summary statistics
  const loadSummary = async () => {
    try {
      const response = await axios.get(`${API}/admin/summary`, { withCredentials: true });
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to load summary:', error);
      // Use mock data for demo
      setSummary({
        restaurants: { total: 147, active: 132, pending: 8, suspended: 7 },
        ngos: { total: 89, active: 81, pending: 5, suspended: 3 },
        volunteers: { total: 234, active: 156, offline: 78 },
        incidents: { failedLogins: 12, lockedAccounts: 3, docExpiries: 15 }
      });
    }
  };

  // Load data based on active tab
  useEffect(() => {
    if (user && user.role === 'super_admin') {
      loadDataForTab();
    }
  }, [activeTab, activeOrgTab, activeUserTab, activeComplianceTab, orgFilters, userFilters, user]);

  const loadDataForTab = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'organizations':
          await loadOrganizations();
          break;
        case 'users':
          await loadUsers();
          break;
        case 'compliance':
          await loadCompliance();
          break;
        case 'audit':
          await loadAuditLogs();
          break;
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizations = async () => {
    try {
      const params = {
        type: activeOrgTab === 'restaurants' ? 'restaurant' : 'ngo',
        ...orgFilters,
        maskPII: adminSettings.maskPII
      };
      const response = await axios.get(`${API}/admin/organizations`, { 
        params,
        withCredentials: true 
      });
      setOrganizations(response.data);
    } catch (error) {
      // Use mock data for demo
      setOrganizations([
        {
          id: '1',
          name: adminSettings.maskPII ? 'G**** R******' : 'Green Restaurant',
          country: 'India',
          city: 'Mumbai',
          status: 'active',
          created: '2024-01-15',
          lastActive: '2024-01-20',
          owner: adminSettings.maskPII ? 'j****@g*****.com' : 'john@gmail.com'
        },
        {
          id: '2',
          name: adminSettings.maskPII ? 'D**** D******' : 'Delhi Delights',
          country: 'India',
          city: 'Delhi',
          status: 'pending',
          created: '2024-01-18',
          lastActive: '2024-01-19',
          owner: adminSettings.maskPII ? 's****@d*****.com' : 'sara@delhi.com'
        }
      ]);
    }
  };

  const loadUsers = async () => {
    try {
      const params = {
        type: activeUserTab,
        ...userFilters,
        maskPII: adminSettings.maskPII
      };
      const response = await axios.get(`${API}/admin/users`, { 
        params,
        withCredentials: true 
      });
      setUsers(response.data);
    } catch (error) {
      // Use mock data for demo
      setUsers([
        {
          id: '1',
          name: adminSettings.maskPII ? 'J**** S****' : 'John Smith',
          email: adminSettings.maskPII ? 'j****@g*****.com' : 'john@gmail.com',
          role: 'restaurant',
          org: adminSettings.maskPII ? 'G**** R******' : 'Green Restaurant',
          status: 'active',
          created: '2024-01-15',
          lastLogin: '2024-01-20'
        },
        {
          id: '2',
          name: adminSettings.maskPII ? 'S**** K****' : 'Sara Khan',
          email: adminSettings.maskPII ? 's****@h*****.org' : 'sara@helpinghands.org',
          role: 'ngo',
          org: adminSettings.maskPII ? 'H**** H****' : 'Helping Hands',
          status: 'locked',
          created: '2024-01-10',
          lastLogin: '2024-01-18'
        },
        {
          id: '3',
          name: adminSettings.maskPII ? 'M**** P****' : 'Mike Patel',
          email: adminSettings.maskPII ? 'm****@v*****.com' : 'mike@volunteer.com',
          role: 'volunteer',
          org: 'Individual',
          status: 'active',
          created: '2024-01-12',
          lastLogin: '2024-01-19'
        }
      ]);
    }
  };

  const loadCompliance = async () => {
    try {
      const params = {
        country: activeComplianceTab,
        maskPII: adminSettings.maskPII
      };
      const response = await axios.get(`${API}/admin/compliance`, { 
        params,
        withCredentials: true 
      });
      setCompliance(response.data);
    } catch (error) {
      // Use mock data for demo
      setCompliance([
        {
          id: '1',
          org: adminSettings.maskPII ? 'G**** R******' : 'Green Restaurant',
          licenseType: 'FSSAI Basic',
          licenseNo: adminSettings.maskPII ? '****567' : '12345567',
          expiry: '2024-12-31',
          status: 'valid',
          reminderSent: false
        },
        {
          id: '2',
          org: adminSettings.maskPII ? 'D**** D******' : 'Delhi Delights',
          licenseType: 'FSSAI State',
          licenseNo: adminSettings.maskPII ? '****890' : '98765890',
          expiry: '2024-02-15',
          status: 'expiring',
          reminderSent: true
        }
      ]);
    }
  };

  const loadAuditLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/audit-logs`, { withCredentials: true });
      setAuditLogs(response.data);
    } catch (error) {
      // Use mock data for demo
      setAuditLogs([
        {
          id: '1',
          timestamp: '2024-01-20T10:30:00Z',
          actor: adminSettings.maskPII ? 'a****@f*****.com' : 'admin@foodconnect.com',
          action: 'suspend_org',
          resource: 'organization:123',
          result: 'success',
          details: '{"reason":"compliance_violation","duration":"30_days"}'
        },
        {
          id: '2',
          timestamp: '2024-01-20T09:15:00Z',
          actor: adminSettings.maskPII ? 'a****@f*****.com' : 'admin@foodconnect.com',
          action: 'force_password_reset',
          resource: 'user:456',
          result: 'success',
          details: '{"notification_sent":true}'
        }
      ]);
    }
  };

  // Admin toggle handlers
  const handleToggleSetting = async (setting) => {
    const newSettings = {
      ...adminSettings,
      [setting]: !adminSettings[setting]
    };

    try {
      await axios.put(`${API}/admin/settings`, newSettings, { withCredentials: true });
      setAdminSettings(newSettings);
      
      // Reload data if PII masking changed
      if (setting === 'maskPII') {
        loadDataForTab();
      }

      toast({
        title: "Settings Updated",
        description: `${setting} ${newSettings[setting] ? 'enabled' : 'disabled'}`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update settings",
        variant: "destructive"
      });
    }
  };

  // Action handlers
  const handleOrgAction = async (action, orgId, data = {}) => {
    if (adminSettings.readOnlyMode && ['suspend', 'reinstate', 'delete'].includes(action)) {
      toast({
        title: "Read-Only Mode",
        description: "Action blocked - read-only mode is enabled",
        variant: "destructive"
      });
      return;
    }

    try {
      await axios.post(`${API}/admin/org-action`, {
        action,
        orgId,
        ...data
      }, { withCredentials: true });

      await logAuditAction(action, 'organization', orgId, data);
      await loadOrganizations();

      toast({
        title: "Success",
        description: `Organization ${action} completed`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to ${action} organization`,
        variant: "destructive"
      });
    }
  };

  const handleUserAction = async (action, userId, data = {}) => {
    if (adminSettings.readOnlyMode && ['lock', 'unlock', 'deactivate'].includes(action)) {
      toast({
        title: "Read-Only Mode",
        description: "Action blocked - read-only mode is enabled",
        variant: "destructive"
      });
      return;
    }

    try {
      await axios.post(`${API}/admin/user-action`, {
        action,
        userId,
        ...data
      }, { withCredentials: true });

      await logAuditAction(action, 'user', userId, data);
      await loadUsers();

      toast({
        title: "Success",
        description: `User ${action} completed`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to ${action} user`,
        variant: "destructive"
      });
    }
  };

  const handleDeleteUser = async (userId, userName, userRole) => {
    if (adminSettings.readOnlyMode) {
      toast({
        title: "Read-Only Mode",
        description: "Delete action blocked - read-only mode is enabled",
        variant: "destructive"
      });
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete ${userName} (${userRole})?\n\nThis will permanently delete:\n- User account\n- ${userRole === 'volunteer' ? 'Profile and assignments' : userRole === 'restaurant' ? 'All food listings' : 'All meal claims'}\n\nThis action cannot be undone!`
    );

    if (!confirmed) return;

    try {
      const response = await axios.delete(`${API}/admin/user/${userId}`, {
        withCredentials: true
      });

      await logAuditAction('delete_user', 'user', userId, { name: userName, role: userRole });
      
      // Reload both summary and data
      await loadSummary();
      await loadUsers();
      await loadOrganizations();

      toast({
        title: "Success",
        description: response.data.message || `User ${userName} deleted successfully`,
        duration: 5000
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || `Failed to delete user`,
        variant: "destructive"
      });
    }
  };

  const handleImpersonate = async (targetId, type) => {
    if (!impersonateReason.trim()) {
      toast({
        title: "Reason Required",
        description: "Please provide a reason for impersonation",
        variant: "destructive"
      });
      return;
    }

    try {
      await axios.post(`${API}/admin/impersonate`, {
        targetId,
        type,
        reason: impersonateReason,
        readOnly: true
      }, { withCredentials: true });

      await logAuditAction('impersonate', type, targetId, { reason: impersonateReason });

      toast({
        title: "Impersonation Logged",
        description: "Read-only impersonation recorded in audit log"
      });

      setImpersonateReason('');
      setShowConfirmModal(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to log impersonation",
        variant: "destructive"
      });
    }
  };

  const handleExport = async (scope, data) => {
    if (!exportReason.trim()) {
      toast({
        title: "Reason Required",
        description: "Please provide a reason for data export",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/export`, {
        scope,
        data,
        reason: exportReason,
        maskPII: adminSettings.maskPII
      }, { 
        withCredentials: true,
        responseType: 'blob'
      });

      // Download file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${scope}_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      await logAuditAction('export_data', scope, null, { reason: exportReason });

      toast({
        title: "Export Complete",
        description: "Data exported successfully"
      });

      setExportReason('');
      setShowConfirmModal(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to export data",
        variant: "destructive"
      });
    }
  };

  const logAuditAction = async (action, resourceType, resourceId, details) => {
    try {
      await axios.post(`${API}/admin/audit-log`, {
        action,
        resourceType,
        resourceId,
        details
      }, { withCredentials: true });
    } catch (error) {
      console.error('Failed to log audit action:', error);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'bg-green-100 text-green-800',
      pending: 'bg-blue-100 text-blue-800',
      suspended: 'bg-gray-100 text-gray-800',
      expired: 'bg-red-100 text-red-800',
      locked: 'bg-red-100 text-red-800',
      valid: 'bg-green-100 text-green-800',
      expiring: 'bg-orange-100 text-orange-800',
      offline: 'bg-gray-100 text-gray-800'
    };

    return (
      <Badge className={`${variants[status] || variants.active} border-0`}>
        {status}
      </Badge>
    );
  };

  const formatAuditDetails = (details) => {
    try {
      if (!details) return 'No details';
      
      // If it's already an object, stringify it
      if (typeof details === 'object') {
        return JSON.stringify(details, null, 2).substring(0, 50) + '...';
      }
      
      // If it's a string, try to parse it as JSON
      const parsed = JSON.parse(details);
      return JSON.stringify(parsed, null, 2).substring(0, 50) + '...';
    } catch (error) {
      // If parsing fails, just return the raw string (truncated)
      if (typeof details === 'string') {
        return details.substring(0, 50) + '...';
      }
      return 'Invalid data';
    }
  };

  // Logout function
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast({
        title: "Success",
        description: "Logged out successfully"
      });
      navigate('/auth');
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to logout",
        variant: "destructive"
      });
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || user.role !== 'super_admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex justify-between items-start">
            {/* Title Section */}
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Super Admin Console</h1>
              <p className="text-gray-600 mt-2">
                Manage organizations and users across Restaurants, NGOs, and Volunteers.
              </p>
            </div>

            {/* Admin Controls and User Menu */}
            <div className="flex items-start space-x-6">
              {/* Admin Toggles */}
              <div className="flex flex-col space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 mr-3">Mask PII</span>
                  <button
                    onClick={() => handleToggleSetting('maskPII')}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      adminSettings.maskPII ? 'bg-green-600' : 'bg-gray-200'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      adminSettings.maskPII ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 mr-3">Read-only Mode</span>
                  <button
                    onClick={() => handleToggleSetting('readOnlyMode')}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      adminSettings.readOnlyMode ? 'bg-green-600' : 'bg-gray-200'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      adminSettings.readOnlyMode ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 mr-3">Danger Zone</span>
                  <button
                    onClick={() => handleToggleSetting('dangerZone')}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      adminSettings.dangerZone ? 'bg-red-600' : 'bg-gray-200'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      adminSettings.dangerZone ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>

              {/* User Menu */}
              <div className="flex flex-col items-end space-y-2">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">
                    {user.name || 'Super Admin'}
                  </p>
                  <p className="text-xs text-gray-500">{user.email}</p>
                </div>
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  size="sm"
                  className="text-gray-700 border-gray-300 hover:bg-gray-50"
                >
                  🚪 Logout
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Restaurants Summary */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Restaurants</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Total</span>
                  <span className="font-semibold">{summary.restaurants.total}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Active</span>
                  <span className="text-green-600 font-semibold">{summary.restaurants.active}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Pending</span>
                  <span className="text-blue-600 font-semibold">{summary.restaurants.pending}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Suspended</span>
                  <span className="text-red-600 font-semibold">{summary.restaurants.suspended}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* NGOs Summary */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">NGOs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Total</span>
                  <span className="font-semibold">{summary.ngos.total}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Active</span>
                  <span className="text-green-600 font-semibold">{summary.ngos.active}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Pending</span>
                  <span className="text-blue-600 font-semibold">{summary.ngos.pending}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Suspended</span>
                  <span className="text-red-600 font-semibold">{summary.ngos.suspended}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Volunteers Summary */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Volunteers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Total</span>
                  <span className="font-semibold">{summary.volunteers.total}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Active</span>
                  <span className="text-green-600 font-semibold">{summary.volunteers.active}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Offline</span>
                  <span className="text-gray-600 font-semibold">{summary.volunteers.offline}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Incidents Summary */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Incidents (24h)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Failed Logins</span>
                  <span className="text-orange-600 font-semibold">{summary.incidents.failedLogins}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Locked Accounts</span>
                  <span className="text-red-600 font-semibold">{summary.incidents.lockedAccounts}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Doc Expiries</span>
                  <span className="text-yellow-600 font-semibold">{summary.incidents.docExpiries}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="organizations">Organizations</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="compliance">Compliance</TabsTrigger>
            <TabsTrigger value="audit">Audit Log</TabsTrigger>
          </TabsList>

          {/* Organizations Tab */}
          <TabsContent value="organizations">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Organizations</CardTitle>
                    <CardDescription>Manage restaurant and NGO organizations</CardDescription>
                  </div>
                  <Button
                    onClick={() => {
                      setConfirmAction(() => () => handleExport('organizations', organizations));
                      setShowConfirmModal(true);
                    }}
                    variant="outline"
                    disabled={adminSettings.readOnlyMode}
                  >
                    Export CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Sub-tabs */}
                <Tabs value={activeOrgTab} onValueChange={setActiveOrgTab} className="mb-4">
                  <TabsList>
                    <TabsTrigger value="restaurants">Restaurants</TabsTrigger>
                    <TabsTrigger value="ngos">NGOs</TabsTrigger>
                  </TabsList>
                </Tabs>

                {/* Filters */}
                <div className="flex flex-wrap gap-4 mb-4">
                  <Input
                    placeholder="Search name/email/domain..."
                    value={orgFilters.search}
                    onChange={(e) => setOrgFilters(prev => ({ ...prev, search: e.target.value }))}
                    className="max-w-xs"
                  />
                  <select
                    value={orgFilters.country}
                    onChange={(e) => setOrgFilters(prev => ({ ...prev, country: e.target.value }))}
                    className="px-3 py-2 border rounded-lg"
                  >
                    <option value="all">All Countries</option>
                    <option value="India">India</option>
                    <option value="Singapore">Singapore</option>
                  </select>
                  <select
                    value={orgFilters.status}
                    onChange={(e) => setOrgFilters(prev => ({ ...prev, status: e.target.value }))}
                    className="px-3 py-2 border rounded-lg"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>

                {/* Organizations Table */}
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-medium">Name</th>
                        <th className="text-left p-3 font-medium">Country</th>
                        <th className="text-left p-3 font-medium">City</th>
                        <th className="text-left p-3 font-medium">Status</th>
                        <th className="text-left p-3 font-medium">Created</th>
                        <th className="text-left p-3 font-medium">Last Active</th>
                        <th className="text-left p-3 font-medium">Owner</th>
                        <th className="text-left p-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {organizations.map((org) => (
                        <tr key={org.id} className="border-b hover:bg-gray-50">
                          <td className="p-3">{org.name}</td>
                          <td className="p-3">{org.country}</td>
                          <td className="p-3">{org.city}</td>
                          <td className="p-3">{getStatusBadge(org.status)}</td>
                          <td className="p-3">{org.created}</td>
                          <td className="p-3">{org.lastActive}</td>
                          <td className="p-3">{org.owner}</td>
                          <td className="p-3">
                            <div className="flex space-x-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedItem(org);
                                  setShowProfileDrawer(true);
                                }}
                              >
                                View
                              </Button>
                              {org.status === 'active' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleOrgAction('suspend', org.id)}
                                  disabled={adminSettings.readOnlyMode}
                                >
                                  Suspend
                                </Button>
                              )}
                              {org.status === 'suspended' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleOrgAction('reinstate', org.id)}
                                  disabled={adminSettings.readOnlyMode}
                                >
                                  Reinstate
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteUser(org.id, org.name, activeOrgTab === 'restaurants' ? 'restaurant' : 'ngo')}
                                disabled={adminSettings.readOnlyMode}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Users</CardTitle>
                    <CardDescription>Manage user accounts across all roles</CardDescription>
                    {adminSettings.maskPII && (
                      <div className="mt-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-xs text-blue-700">
                          🔒 <strong>PII Masking is ON</strong> - Email addresses are masked. 
                          Toggle "Mask PII" in the top-right to see full email addresses.
                        </p>
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={() => {
                      setConfirmAction(() => () => handleExport('users', users));
                      setShowConfirmModal(true);
                    }}
                    variant="outline"
                    disabled={adminSettings.readOnlyMode}
                  >
                    Export CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Sub-tabs */}
                <Tabs value={activeUserTab} onValueChange={setActiveUserTab} className="mb-4">
                  <TabsList>
                    <TabsTrigger value="restaurant-users">Restaurant Users</TabsTrigger>
                    <TabsTrigger value="ngo-users">NGO Users</TabsTrigger>
                    <TabsTrigger value="volunteers">Volunteers</TabsTrigger>
                  </TabsList>
                </Tabs>

                {/* Filters */}
                <div className="flex flex-wrap gap-4 mb-4">
                  <Input
                    placeholder="Search name/email..."
                    value={userFilters.search}
                    onChange={(e) => setUserFilters(prev => ({ ...prev, search: e.target.value }))}
                    className="max-w-xs"
                  />
                  <select
                    value={userFilters.status}
                    onChange={(e) => setUserFilters(prev => ({ ...prev, status: e.target.value }))}
                    className="px-3 py-2 border rounded-lg"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="locked">Locked</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>

                {/* Users Table */}
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-medium">Name</th>
                        <th className="text-left p-3 font-medium">Email</th>
                        <th className="text-left p-3 font-medium">Role</th>
                        <th className="text-left p-3 font-medium">Organization</th>
                        <th className="text-left p-3 font-medium">Status</th>
                        <th className="text-left p-3 font-medium">Created</th>
                        <th className="text-left p-3 font-medium">Last Login</th>
                        <th className="text-left p-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user.id} className="border-b hover:bg-gray-50">
                          <td className="p-3">{user.name}</td>
                          <td className="p-3">
                            <span className={adminSettings.maskPII ? 'font-mono text-sm' : ''}>
                              {user.email}
                            </span>
                            {adminSettings.maskPII && (
                              <span className="ml-2 text-xs text-gray-500" title="PII Masked">🔒</span>
                            )}
                          </td>
                          <td className="p-3 capitalize">{user.role}</td>
                          <td className="p-3">{user.org}</td>
                          <td className="p-3">{getStatusBadge(user.status)}</td>
                          <td className="p-3">{user.created}</td>
                          <td className="p-3">{user.lastLogin}</td>
                          <td className="p-3">
                            <div className="flex space-x-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUserAction('reset_password', user.id)}
                                disabled={adminSettings.readOnlyMode}
                              >
                                Reset Pwd
                              </Button>
                              {user.status === 'active' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleUserAction('lock', user.id)}
                                  disabled={adminSettings.readOnlyMode}
                                >
                                  Lock
                                </Button>
                              )}
                              {user.status === 'locked' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleUserAction('unlock', user.id)}
                                  disabled={adminSettings.readOnlyMode}
                                >
                                  Unlock
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteUser(user.id, user.name, user.role)}
                                disabled={adminSettings.readOnlyMode}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Compliance Tab */}
          <TabsContent value="compliance">
            <Card>
              <CardHeader>
                <CardTitle>Compliance Management</CardTitle>
                <CardDescription>Monitor licenses and regulatory compliance</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Sub-tabs */}
                <Tabs value={activeComplianceTab} onValueChange={setActiveComplianceTab} className="mb-4">
                  <TabsList>
                    <TabsTrigger value="india">India</TabsTrigger>
                    <TabsTrigger value="singapore">Singapore</TabsTrigger>
                  </TabsList>
                </Tabs>

                {/* Compliance Table */}
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-medium">Organization</th>
                        <th className="text-left p-3 font-medium">License Type</th>
                        <th className="text-left p-3 font-medium">License No.</th>
                        <th className="text-left p-3 font-medium">Expiry</th>
                        <th className="text-left p-3 font-medium">Status</th>
                        <th className="text-left p-3 font-medium">Reminder Sent</th>
                        <th className="text-left p-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {compliance.map((item) => (
                        <tr key={item.id} className="border-b hover:bg-gray-50">
                          <td className="p-3">{item.org}</td>
                          <td className="p-3">{item.licenseType}</td>
                          <td className="p-3">{item.licenseNo}</td>
                          <td className="p-3">{item.expiry}</td>
                          <td className="p-3">{getStatusBadge(item.status)}</td>
                          <td className="p-3">{item.reminderSent ? '✅' : '❌'}</td>
                          <td className="p-3">
                            <div className="flex space-x-2">
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={adminSettings.readOnlyMode}
                              >
                                Send Reminder
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={adminSettings.readOnlyMode}
                              >
                                Mark Verified
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Audit Log</CardTitle>
                    <CardDescription>System activity and administrative actions</CardDescription>
                  </div>
                  <Button
                    onClick={() => {
                      setConfirmAction(() => () => handleExport('audit', auditLogs));
                      setShowConfirmModal(true);
                    }}
                    variant="outline"
                  >
                    Export CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-medium">Timestamp</th>
                        <th className="text-left p-3 font-medium">Actor</th>
                        <th className="text-left p-3 font-medium">Action</th>
                        <th className="text-left p-3 font-medium">Resource</th>
                        <th className="text-left p-3 font-medium">Result</th>
                        <th className="text-left p-3 font-medium">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogs.map((log) => (
                        <tr key={log.id} className="border-b hover:bg-gray-50">
                          <td className="p-3">{new Date(log.timestamp).toLocaleString()}</td>
                          <td className="p-3">{log.actor}</td>
                          <td className="p-3">{log.action}</td>
                          <td className="p-3">{log.resource}</td>
                          <td className="p-3">{getStatusBadge(log.result === 'success' ? 'active' : 'expired')}</td>
                          <td className="p-3">
                            <code className="text-xs bg-gray-100 p-1 rounded" title={formatAuditDetails(log.details)}>
                              {formatAuditDetails(log.details)}
                            </code>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-semibold mb-4">Confirm Action</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason (required)
                </label>
                <Input
                  value={exportReason || impersonateReason}
                  onChange={(e) => {
                    setExportReason(e.target.value);
                    setImpersonateReason(e.target.value);
                  }}
                  placeholder="Enter reason for this action..."
                />
              </div>
              <div className="flex items-center space-x-2">
                <input type="checkbox" id="acknowledge" className="w-4 h-4" />
                <label htmlFor="acknowledge" className="text-sm text-gray-600">
                  I acknowledge PII handling responsibilities
                </label>
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <Button
                onClick={() => setShowConfirmModal(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (confirmAction) {
                    confirmAction();
                  }
                }}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Drawer */}
      {showProfileDrawer && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-end z-50">
          <div className="bg-white w-full md:w-96 h-full overflow-y-auto">
            <div className="p-4 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Organization Profile</h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowProfileDrawer(false)}
                >
                  ✕
                </Button>
              </div>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <h3 className="font-medium mb-2">Organization Details</h3>
                <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                  <p><strong>Name:</strong> {selectedItem.name}</p>
                  <p><strong>Country:</strong> {selectedItem.country}</p>
                  <p><strong>City:</strong> {selectedItem.city}</p>
                  <p><strong>Status:</strong> {getStatusBadge(selectedItem.status)}</p>
                  <p><strong>Created:</strong> {selectedItem.created}</p>
                  <p><strong>Owner:</strong> {selectedItem.owner}</p>
                </div>
              </div>

              <div>
                <h3 className="font-medium mb-2">Statistics</h3>
                <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                  <p><strong>Active Listings:</strong> 12</p>
                  <p><strong>Completed Deliveries:</strong> 45</p>
                  <p><strong>Success Rate:</strong> 98%</p>
                </div>
              </div>

              <div className="space-y-2">
                <Button className="w-full" variant="outline">
                  Reset Owner Password
                </Button>
                <Button 
                  className="w-full" 
                  variant="outline"
                  onClick={() => {
                    setShowConfirmModal(true);
                    setConfirmAction(() => () => handleImpersonate(selectedItem.id, 'organization'));
                  }}
                >
                  Impersonate (Read-only)
                </Button>
                <Button className="w-full" variant="outline">
                  Export Org Data
                </Button>
                {adminSettings.dangerZone && (
                  <Button className="w-full bg-red-600 hover:bg-red-700">
                    Delete Organization
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;